/**
 * Frontend-typer for LabelDesigner (canvas-editor).
 * Skal matche BizClick.WMS.Shared.DTOs.Reporting.LabelDesignDTOs.
 */

export type LabelElementType =
  | 'Text'
  | 'Barcode'
  | 'QrCode'
  | 'Image'
  | 'Rectangle'
  | 'Line'
  | 'Table';

export type TableColumnWidthMode = 'Relative' | 'ConstantMm';

/**
 * Kolonnedefinisjon for et Table-element (dokumentmodus).
 * Field er en mal med plassholdere som løses per rad, f.eks. "{{ItemNo}}".
 */
export interface TableColumnDef {
  header: string;
  field: string;
  widthMode?: TableColumnWidthMode;
  width?: number;
  align?: TextAlign;
  bold?: boolean;
  /** Render celleverdien også som Code128-strekkode under teksten. */
  showBarcode?: boolean;
  /** Render en tom avkrysningsboks i stedet for tekst. */
  checkboxColumn?: boolean;
}

export type BarcodeFormat = 'Code128' | 'Code39' | 'Ean13' | 'Ean8' | 'Gs1128';

export type TextAlign = 'Left' | 'Center' | 'Right';

/**
 * Et enkelt element på etiketten. Posisjoner/dimensjoner i millimeter
 * relativt til øvre venstre hjørne av etiketten.
 */
export interface LabelElement {
  id: string;
  type: LabelElementType;

  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;

  // Text
  text?: string;
  fontSize?: number;
  bold?: boolean;
  italic?: boolean;
  fontFamily?: string;
  textAlign?: TextAlign;
  color?: string;

  // Barcode / QR
  value?: string;
  barcodeFormat?: BarcodeFormat;
  showText?: boolean;

  // Image
  imageSource?: string;

  // Shape
  fillColor?: string;
  borderColor?: string;
  borderWidth?: number;

  // Table (dokumentmodus — ett Table-element per layout)
  /** Nøkkel inn i rad-samlingene, f.eks. "PListLines", "PackingSlipLines". */
  dataSource?: string;
  columns?: TableColumnDef[];
  /** Radfelt å sortere stigende på (f.eks. "BinCode"). */
  sortBy?: string;
  headerBackground?: string;
  minRowHeightMm?: number;
  cellPaddingMm?: number;
}

export interface LabelLayout {
  version: number;
  widthMm: number;
  heightMm: number;
  backgroundColor?: string;
  /**
   * Hvis true, sender backend `fit_to_page=true` til PrintNode slik at skriveren
   * skalerer PDF-en til faktisk media-størrelse. Anbefales kun for ren tekst —
   * strekkoder kan bli uskannbare ved skalering. Default false.
   */
  fitToPage?: boolean;
  elements: LabelElement[];
}

export interface CustomLabelPreviewRequest {
  layout: LabelLayout;
  sampleData?: Record<string, string | null | undefined>;
  /** Eksempel-rader for Table-elementer i preview, nøklet på dataSource-navn. */
  sampleRows?: Record<string, Array<Record<string, string | null>>>;
}

/**
 * Autoritativ plassholder-modell — matcher backend LabelPlaceholder DTO.
 * Hentes dynamisk via labelPlaceholderService.
 */
export interface LabelPlaceholder {
  key: string;
  label: string;
  exampleValue: string;
  category: string;
  applicableReportTypes: number[];
}

/**
 * En kolonne i en tabell — kan settes inn som plassholder `{{Table.Column}}`.
 * Matcher backend TableColumnDTO.
 */
export interface TableColumn {
  name: string;
  sqlType: string;
  nullable: boolean;
  maxLength?: number | null;
}

/**
 * Skjema for én tabell eksponert i plassholder-picker.
 * Matcher backend TableSchemaDTO.
 */
export interface TableSchema {
  tableName: string;
  displayName: string;
  columns: TableColumn[];
}

/**
 * Kontekst for DB-oppslag i etikett-preview / -utskrift.
 * Alle felter er valgfrie — kun satt felt trigger oppslag i tilhørende tabell.
 * Matcher backend LabelContext.
 */
export interface LabelContext {
  itemNo?: string;
  orderNo?: number;
  orderLineNo?: number;
  pOrderNo?: number;
  pOrderLineNo?: number;
  warehouseID?: number;
  binCode?: string;
  /** PList.ID — trigger oppslag i PListSSCC (gir SSCC, PalletNo) og PListLines-samlingen. */
  pListID?: number;
  /** OrderSSCC.SSCC — eksakt pall; kjeder videre til Orders/OrderLine for samme pall. */
  sscc?: string;
}

/**
 * Fallback-liste brukt hvis API-kallet feiler (offline/utvikling uten API).
 * Den faktiske listen hentes fra backend — se labelPlaceholderService.
 */
export const FALLBACK_PLACEHOLDERS: LabelPlaceholder[] = [
  { key: 'ItemNo', label: 'Varenummer', exampleValue: 'ART-12345', category: 'Vare', applicableReportTypes: [] },
  { key: 'ItemName', label: 'Varenavn', exampleValue: 'Skrue M6 x 30mm', category: 'Vare', applicableReportTypes: [] },
  { key: 'BinCode', label: 'Lokasjon', exampleValue: 'A-01-02-03', category: 'Lager', applicableReportTypes: [] },
  { key: 'BatchNo', label: 'Batchnummer', exampleValue: 'LOT-2026-A', category: 'Batch/serie', applicableReportTypes: [] },
  { key: 'SerialNo', label: 'Serienummer', exampleValue: 'SN-9876', category: 'Batch/serie', applicableReportTypes: [] },
  { key: 'Quantity', label: 'Antall', exampleValue: '100', category: 'Batch/serie', applicableReportTypes: [] },
  { key: 'CompanyName', label: 'Firmanavn', exampleValue: 'Demo Lager AS', category: 'Firma', applicableReportTypes: [] }
];

export function createEmptyLayout(widthMm: number = 100, heightMm: number = 50): LabelLayout {
  return {
    version: 1,
    widthMm,
    heightMm,
    elements: []
  };
}

/**
 * Genererer eksempel-rader for Table-elementer til forhåndsvisning, nøklet på
 * dataSource. Verdier plukkes fra et felles dummysett per feltnavn; ukjente
 * felter får feltnavnet som verdi så designeren ser hva som mangler binding.
 */
export function generateSampleRows(
  layout: LabelLayout
): Record<string, Array<Record<string, string | null>>> | undefined {
  const tables = layout.elements.filter((el) => el.type === 'Table' && el.dataSource);
  if (tables.length === 0) return undefined;

  const sampleValue = (field: string, rowNo: number): string => {
    const samples: Record<string, string[]> = {
      LineNo: ['1', '2', '3'],
      PListLineNo: ['1', '2', '3'],
      BinCode: ['A-01-01', 'A-02-05', 'B-11-03'],
      Location: ['A-01-01', 'A-02-05', 'B-11-03'],
      SourceLocation: ['MOTTAK', 'MOTTAK', 'MOTTAK'],
      DestinationLocation: ['A-01-01', 'A-02-05', 'B-11-03'],
      ItemNo: ['DEMO-001', 'DEMO-002', 'DEMO-003'],
      ItemDescription: ['Demo Produkt 1', 'Demo Produkt 2', 'Demo Produkt 3'],
      Qty: ['10', '5', '8'],
      QtyToPick: ['10', '5', '8'],
      QtyOrdered: ['10', '5', '8'],
      QtyPicked: ['10', '5', '8'],
      ExpectedQty: ['25', '12', '40'],
      Quantity: ['10', '5', '8'],
      BatchNo: ['B2026-001', '', 'B2026-007'],
      SerialNo: ['', '', ''],
      OrderNo: ['99901', '99901', '99902'],
      ItemBarCode: ['7032069001234', '', '']
    };
    return samples[field]?.[rowNo] ?? field;
  };

  const result: Record<string, Array<Record<string, string | null>>> = {};
  for (const table of tables) {
    // Radfelter = alle {{Felt}}-referanser i kolonnene + sortBy
    const fields = new Set<string>();
    (table.columns ?? []).forEach((c) => {
      // regex.exec-løkke i stedet for matchAll — TS-target krever downlevelIteration for iteratorer
      const re = /\{\{(\w+)\}\}/g;
      let m: RegExpExecArray | null;
      while ((m = re.exec(c.field || '')) !== null) fields.add(m[1]);
    });
    if (table.sortBy) fields.add(table.sortBy);

    result[table.dataSource!] = [0, 1, 2].map((rowNo) => {
      const row: Record<string, string | null> = {};
      fields.forEach((f) => { row[f] = sampleValue(f, rowNo); });
      return row;
    });
  }
  return result;
}

export function createElement(
  type: LabelElementType,
  x: number,
  y: number
): LabelElement {
  const id = `el-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const base: LabelElement = { id, type, x, y, width: 30, height: 10 };

  switch (type) {
    case 'Text':
      return { ...base, text: 'Ny tekst', fontSize: 10, textAlign: 'Left' };
    case 'Barcode':
      return { ...base, value: '{{ItemNo}}', barcodeFormat: 'Code128', showText: true, height: 15 };
    case 'QrCode':
      return { ...base, value: '{{ItemNo}}', width: 20, height: 20 };
    case 'Image':
      return { ...base, imageSource: 'logo', width: 25, height: 15 };
    case 'Rectangle':
      return { ...base, borderColor: '#000000', borderWidth: 0.3 };
    case 'Line':
      return { ...base, borderColor: '#000000', borderWidth: 0.3, height: 0.3 };
    case 'Table':
      // Dokumentmodus: fornuftig startoppsett — kolonner tilpasses i PropertyPanel
      return {
        ...base,
        width: 180,
        height: 180,
        dataSource: 'PListLines',
        columns: [
          { header: 'Linje', field: '{{LineNo}}', widthMode: 'ConstantMm', width: 12 },
          { header: 'Artikkelnr', field: '{{ItemNo}}', widthMode: 'Relative', width: 2 },
          { header: 'Beskrivelse', field: '{{ItemDescription}}', widthMode: 'Relative', width: 3 },
          { header: 'Antall', field: '{{Qty}}', widthMode: 'ConstantMm', width: 18, align: 'Right' }
        ]
      };
  }
}
